import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { AlertTriangle, CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, publicMedia } from "@/api";
import { pick, useLang } from "@/i18n";
import { GoldImage } from "@/components/GoldImage";
import { PlaceAutocompleteInput } from "@/components/PlaceAutocompleteInput";
import { GmapsProvider } from "@/components/builder/gmaps";

const ROME_BIAS = { lat: 41.9028, lng: 12.4964 };

/* Anteprima percorso con Google Directions (stessa chiave/integrazione del builder),
 * mostrata quando partenza e destinazione sono luoghi selezionati dai suggerimenti. */
const RoutePreviewInner = ({ from, to }) => {
    const routesLib = useMapsLibrary("routes");
    const { t } = useLang();
    const [route, setRoute] = useState(null);
    useEffect(() => {
        if (!routesLib?.DirectionsService || !from?.lat || !to?.lat) { setRoute(null); return undefined; }
        let alive = true;
        try {
            new routesLib.DirectionsService().route(
                { origin: { lat: from.lat, lng: from.lng }, destination: { lat: to.lat, lng: to.lng }, travelMode: "DRIVING" },
                (res, status) => { if (alive && status === "OK" && res?.routes?.[0]?.legs?.[0]) setRoute(res.routes[0].legs[0]); },
            );
        } catch { /* anteprima non disponibile: il modulo resta pienamente funzionante */ }
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routesLib, from?.lat, from?.lng, to?.lat, to?.lng]);
    if (!route) return null;
    return <SumRow label={t.transfers.qRoute} value={`${route.distance.text} · ~${route.duration.text}`} testId="trq-route" />;
};
const RoutePreview = (props) => <GmapsProvider><RoutePreviewInner {...props} /></GmapsProvider>;

const HUB_CATEGORIES = ["transfer-fco", "transfer-cia", "transfer-civitavecchia"];

const L = ({ children }) => <span className="text-[10px] uppercase tracking-[0.18em] text-amber-500/90 font-semibold">{children}</span>;

const SumRow = ({ label, value, testId }) => (
    <div className="flex items-baseline justify-between gap-4 py-1.5" data-testid={testId}>
        <span className="text-[10px] uppercase tracking-[0.16em] text-stone-500">{label}</span>
        <span className="text-right text-xs font-medium text-stone-200">{value}</span>
    </div>
);

/** Modulo di richiesta transfer. Con ?transfer=<id> i dati del servizio arrivano
 *  dal gestionale in sola lettura (prezzo, veicolo, capacità, inclusioni…): il
 *  cliente inserisce SOLO i dati del proprio viaggio. La richiesta resta
 *  "in attesa di conferma" finché l'operatore non la verifica nel gestionale. */
export const TransferQuoteForm = ({ categories }) => {
    const { lang, t } = useLang();
    const [params] = useSearchParams();
    const tx = t.transfers;
    const [form, setForm] = useState(() => ({
        category: params.get("cat") || "transfer-fco",
        direction: "arrival",
        address: "",
        pickup: params.get("pickup") || "",
        dropoff: params.get("destination") || "",
        date: params.get("date") || "",
        time: params.get("time") || "",
        flight: "",
        round_trip: false, return_date: "", return_time: "", return_flight: "",
        passengers: parseInt(params.get("passengers") || "2", 10) || 2,
        big_bags: parseInt(params.get("bags") || "0", 10) || 0,
        small_bags: 0,
        vehicle: ["car", "minivan", "sprinter"].includes(params.get("vehicle")) ? params.get("vehicle") : "car",
        notes: "", first_name: "", last_name: "", customer_email: "", customer_phone: "", privacy_consent: false,
    }));
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(null);
    const [selected, setSelected] = useState(null);
    const [places, setPlaces] = useState({ pickup: null, dropoff: null, address: null }); // luoghi scelti dai suggerimenti Google
    const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
    const setPlace = (k) => (p) => setPlaces((s) => ({ ...s, [k]: p }));

    /* Dalla pagina dettaglio: /transfer?transfer=<id> → richiesta legata a quel transfer. */
    const transferParam = params.get("transfer");
    useEffect(() => {
        if (!transferParam) { setSelected(null); return; }
        api.get(`/public/transfers/${encodeURIComponent(transferParam)}`)
            .then((r) => {
                setSelected(r.data);
                if (r.data.category_slug) set("category", r.data.category_slug);
            })
            .catch(() => setSelected(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transferParam]);

    /* Click sulle card categoria → il modulo segue l'hub selezionato (modalità libera). */
    const catParam = params.get("cat");
    useEffect(() => {
        if (!transferParam && catParam && categories.some((c) => c.slug === catParam)) set("category", catParam);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catParam, categories, transferParam]);

    const isHub = HUB_CATEGORIES.includes(form.category);
    const overCap = selected?.capacity && Number(form.passengers) > selected.capacity;
    const cls = (k) => `input-lux mt-1 ${errors[k] ? "!border-red-400/70" : ""}`;

    const submit = async (e) => {
        e.preventDefault();
        const errs = {};
        if (selected) {
            if (form.address.trim().length < 2) errs.address = true;
        } else {
            if (form.pickup.trim().length < 2) errs.pickup = true;
            if (form.dropoff.trim().length < 2) errs.dropoff = true;
        }
        if (!form.date) errs.date = true;
        if (!form.time) errs.time = true;
        if (form.round_trip && !form.return_date) errs.return_date = true;
        if (form.round_trip && !form.return_time) errs.return_time = true;
        if (form.first_name.trim().length < 2) errs.first_name = true;
        if (form.last_name.trim().length < 2) errs.last_name = true;
        if (!form.customer_email.includes("@")) errs.customer_email = true;
        if (form.customer_phone.trim().length < 5) errs.customer_phone = true;
        if (!form.privacy_consent) errs.privacy_consent = true;
        setErrors(errs);
        if (Object.keys(errs).length) { toast.error(tx.qRequired); return; }
        setSending(true);
        try {
            const r = await api.post("/public/transfers/request", {
                ...form,
                transfer_id: selected?.id || null,
                passengers: parseInt(form.passengers, 10) || 1,
                big_bags: parseInt(form.big_bags, 10) || 0,
                small_bags: parseInt(form.small_bags, 10) || 0,
                pickup_place: places.pickup,
                dropoff_place: places.dropoff,
                address_place: places.address,
                language: lang,
                origin_url: window.location.origin,
            });
            setDone(r.data);
        } catch (err) {
            toast.error(apiError(err, lang));
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <div className="mt-14 rounded-2xl border border-amber-500/25 bg-[#121216] p-8 text-center" data-testid="trq-success">
                <CheckCircle2 size={34} className="mx-auto text-amber-400" />
                <h3 className="mt-4 font-display text-2xl text-amber-50">{tx.qSuccessTitle}</h3>
                <p className="mx-auto mt-2 max-w-md text-sm text-stone-300 leading-relaxed">{tx.qSuccessMsg}</p>
                <p className="mt-4 text-xs uppercase tracking-[0.2em] text-stone-500">{tx.qCode}</p>
                <p className="mt-1 font-mono-accent text-xl font-bold text-amber-300" data-testid="trq-code">{done.code}</p>
                <button data-testid="trq-again" onClick={() => setDone(null)}
                    className="mt-6 rounded-full border border-amber-500/50 px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-200 transition-colors hover:bg-amber-400/10">
                    {tx.qAgain}
                </button>
            </div>
        );
    }

    const serviceName = selected ? pick(selected.name, lang) : null;
    const serviceFrom = selected ? pick(selected.from, lang) : null;
    const serviceTo = selected ? pick(selected.to, lang) : null;
    const price = selected?.price != null && selected.price !== "" ? `€ ${Number(selected.price).toLocaleString("it-IT")}` : null;
    const bagsTotal = (parseInt(form.big_bags, 10) || 0) + (parseInt(form.small_bags, 10) || 0);

    return (
        <form onSubmit={submit} className="mt-14 rounded-2xl border border-amber-500/25 bg-[#121216] p-6 sm:p-8" data-testid="transfer-quote-form">
            <h2 className="font-display text-2xl sm:text-3xl text-amber-50">{tx.quoteTitle}</h2>
            <p className="mt-2 max-w-2xl text-xs text-stone-400 leading-relaxed">{tx.quoteSub}</p>

            {/* SCHEDA SERVIZIO IN SOLA LETTURA (dati dal gestionale) */}
            {selected && (
                <div className="mt-6 overflow-hidden rounded-xl border border-amber-500/20 bg-black/30" data-testid="trq-service">
                    <div className="flex flex-col gap-4 p-4 sm:flex-row">
                        <GoldImage src={publicMedia(selected.image_url, true)} alt={serviceName} className="h-28 w-full shrink-0 rounded-lg object-cover sm:w-40" />
                        <div className="min-w-0 flex-1">
                            <p className="text-[9px] uppercase tracking-[0.2em] text-amber-500/80">{tx.qService}</p>
                            <p className="mt-1 font-display text-lg text-amber-50" data-testid="trq-service-name">{serviceName}</p>
                            {(serviceFrom || serviceTo) && (
                                <p className="mt-0.5 text-[11px] text-amber-200/80">{serviceFrom}{serviceTo ? ` → ${serviceTo}` : ""}</p>
                            )}
                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-stone-400">
                                {selected.duration && <span>{tx.fldDuration}: <b className="text-stone-200">{pick(selected.duration, lang)}</b></span>}
                                {selected.vehicle && <span>{tx.fldVehicle}: <b className="text-stone-200">{selected.vehicle}</b></span>}
                                {selected.capacity != null && <span data-testid="trq-service-capacity">{tx.fldPassengers}: <b className="text-stone-200">{selected.capacity}</b></span>}
                                {selected.luggage != null && <span>{tx.fldLuggage}: <b className="text-stone-200">{pick(selected.luggage, lang)}</b></span>}
                            </div>
                            {selected.includes?.length > 0 && (
                                <p className="mt-2 text-[10px] text-stone-500">{tx.fldIncludes}: {selected.includes.map((s) => pick(s, lang)).join(" · ")}</p>
                            )}
                        </div>
                        {price && (
                            <div className="shrink-0 text-right">
                                <p className="font-mono-accent text-xl font-bold text-amber-300" data-testid="trq-service-price">{price}</p>
                                {selected.priceType && <p className="text-[10px] text-stone-500">{selected.priceType}</p>}
                            </div>
                        )}
                    </div>
                    <p className="border-t border-amber-500/10 px-4 py-2 text-[9px] uppercase tracking-[0.16em] text-stone-600">{tx.qReadonly}</p>
                </div>
            )}

            {/* DIREZIONE (solo transfer da/per hub selezionato) */}
            {selected && isHub && (
                <div className="mt-5 flex gap-2" data-testid="trq-direction">
                    {[["arrival", tx.qDirArrival], ["departure", tx.qDirDeparture]].map(([v, lbl]) => (
                        <button key={v} type="button" data-testid={`trq-dir-${v}`} onClick={() => set("direction", v)} aria-pressed={form.direction === v}
                            className={`rounded-full border px-5 py-2 text-[10px] font-bold uppercase tracking-[0.12em] transition-colors ${form.direction === v ? "border-amber-400 bg-amber-400 text-black" : "border-amber-500/40 text-amber-200 hover:bg-amber-400/10"}`}>
                            {lbl}
                        </button>
                    ))}
                </div>
            )}

            {/* DATI DEL VIAGGIO DEL CLIENTE */}
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {!selected && (
                    <label className="block">
                        <L>{tx.qHub} *</L>
                        <select data-testid="trq-hub" className={cls("category")} value={form.category} onChange={(e) => set("category", e.target.value)}>
                            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label[lang] || c.label.it}</option>)}
                        </select>
                    </label>
                )}
                {selected ? (
                    <label className="block sm:col-span-2">
                        <L>{form.direction === "arrival" ? tx.qAddressTo : tx.qAddressFrom} *</L>
                        <PlaceAutocompleteInput testId="trq-address" inputClass={cls("address")}
                            placeholder={form.direction === "arrival" ? tx.qAddressToPh : tx.qAddressFromPh}
                            value={form.address} onChange={(v) => set("address", v)} onSelectPlace={setPlace("address")} bias={ROME_BIAS} />
                    </label>
                ) : (
                    <>
                        <label className="block">
                            <L>{tx.qPickup} *</L>
                            <PlaceAutocompleteInput testId="trq-pickup" inputClass={cls("pickup")} placeholder={tx.qPickupPh}
                                value={form.pickup} onChange={(v) => set("pickup", v)} onSelectPlace={setPlace("pickup")} bias={ROME_BIAS} />
                        </label>
                        <label className="block">
                            <L>{tx.qDrop} *</L>
                            <PlaceAutocompleteInput testId="trq-dropoff" inputClass={cls("dropoff")} placeholder={tx.qDropPh}
                                value={form.dropoff} onChange={(v) => set("dropoff", v)} onSelectPlace={setPlace("dropoff")} bias={ROME_BIAS} />
                        </label>
                    </>
                )}
                <label className="block">
                    <L>{tx.qDate} *</L>
                    <input data-testid="trq-date" type="date" className={`${cls("date")} [color-scheme:dark]`} value={form.date} onChange={(e) => set("date", e.target.value)} />
                </label>
                <label className="block">
                    <L>{selected && isHub ? (form.direction === "arrival" ? tx.qTimeLanding : tx.qTimePickup) : tx.qTime} *</L>
                    <input data-testid="trq-time" type="time" className={`${cls("time")} [color-scheme:dark]`} value={form.time} onChange={(e) => set("time", e.target.value)} />
                </label>
                {(!selected || isHub) && (
                    <label className="block">
                        <L>{tx.qFlight}</L>
                        <input data-testid="trq-flight" className={cls("flight")} placeholder={tx.qFlightPh} value={form.flight} onChange={(e) => set("flight", e.target.value)} />
                    </label>
                )}
                <label className="block">
                    <L>{tx.qPax} *</L>
                    <input data-testid="trq-pax" type="number" min="1" max="30" className={cls("passengers")} value={form.passengers} onChange={(e) => set("passengers", e.target.value)} />
                </label>
                <label className="block">
                    <L>{tx.qBagsBig}</L>
                    <input data-testid="trq-bags-big" type="number" min="0" max="20" className={cls("big_bags")} value={form.big_bags} onChange={(e) => set("big_bags", e.target.value)} />
                </label>
                <label className="block">
                    <L>{tx.qBagsSmall}</L>
                    <input data-testid="trq-bags-small" type="number" min="0" max="20" className={cls("small_bags")} value={form.small_bags} onChange={(e) => set("small_bags", e.target.value)} />
                </label>
                {!selected && (
                    <label className="block">
                        <L>{tx.qVehicle}</L>
                        <select data-testid="trq-vehicle" className={cls("vehicle")} value={form.vehicle} onChange={(e) => set("vehicle", e.target.value)}>
                            {Object.entries(t.builder.transports).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </label>
                )}
            </div>

            {/* ANDATA E RITORNO */}
            <div className="mt-4">
                <button type="button" data-testid="trq-roundtrip" onClick={() => set("round_trip", !form.round_trip)} aria-pressed={form.round_trip}
                    className={`inline-flex items-center gap-2.5 rounded-full border px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] transition-colors ${form.round_trip ? "border-amber-400 bg-amber-400 text-black" : "border-amber-500/40 text-amber-200 hover:bg-amber-400/10"}`}>
                    {tx.qRoundTrip}
                </button>
                {form.round_trip && (
                    <div className="mt-4 grid gap-4 sm:grid-cols-3" data-testid="trq-return-fields">
                        <label className="block">
                            <L>{tx.qReturnDate} *</L>
                            <input data-testid="trq-return-date" type="date" className={`${cls("return_date")} [color-scheme:dark]`} value={form.return_date} onChange={(e) => set("return_date", e.target.value)} />
                        </label>
                        <label className="block">
                            <L>{tx.qReturnTime} *</L>
                            <input data-testid="trq-return-time" type="time" className={`${cls("return_time")} [color-scheme:dark]`} value={form.return_time} onChange={(e) => set("return_time", e.target.value)} />
                        </label>
                        <label className="block">
                            <L>{tx.qReturnFlight}</L>
                            <input data-testid="trq-return-flight" className={cls("return_flight")} placeholder={tx.qFlightPh} value={form.return_flight} onChange={(e) => set("return_flight", e.target.value)} />
                        </label>
                    </div>
                )}
            </div>

            {/* CONTATTI */}
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <label className="block">
                    <L>{tx.qFirstName} *</L>
                    <input data-testid="trq-first-name" className={cls("first_name")} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
                </label>
                <label className="block">
                    <L>{tx.qLastName} *</L>
                    <input data-testid="trq-last-name" className={cls("last_name")} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
                </label>
                <label className="block">
                    <L>{tx.qEmail} *</L>
                    <input data-testid="trq-email" type="email" className={cls("customer_email")} value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} />
                </label>
                <label className="block">
                    <L>{tx.qPhone} *</L>
                    <input data-testid="trq-phone" className={cls("customer_phone")} value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} />
                </label>
            </div>

            <label className="mt-4 block">
                <L>{tx.qNotes}</L>
                <textarea data-testid="trq-notes" rows={2} className={cls("notes")} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </label>

            {/* AVVISO CAPIENZA */}
            {overCap && (
                <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-400/50 bg-amber-400/10 p-4" data-testid="trq-capacity-warning">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-300" />
                    <p className="text-xs leading-relaxed text-amber-100/90">{tx.qCapWarn.replace("{n}", selected.capacity)}</p>
                </div>
            )}

            {/* RIEPILOGO PRIMA DELL'INVIO */}
            <div className="mt-6 rounded-xl border border-amber-500/20 bg-black/30 p-5" data-testid="trq-summary">
                <p className="text-[10px] uppercase tracking-[0.2em] text-amber-400/90 font-semibold">{tx.qSummaryTitle}</p>
                <div className="mt-2 divide-y divide-amber-500/5">
                    <SumRow label={tx.qSelected} value={serviceName || (categories.find((c) => c.slug === form.category)?.label[lang] || form.category)} testId="trq-sum-service" />
                    <SumRow label={tx.qSumWhen} value={form.date ? `${form.date} · ${form.time || "—"}${form.round_trip && form.return_date ? `  +  ${form.return_date} · ${form.return_time || "—"}` : ""}` : "—"} testId="trq-sum-when" />
                    <SumRow label={tx.qSumClient} value={`${form.first_name} ${form.last_name}`.trim() || "—"} testId="trq-sum-client" />
                    <SumRow label={tx.qSumPax} value={form.passengers} testId="trq-sum-pax" />
                    <SumRow label={tx.qSumBags} value={`${form.big_bags || 0} + ${form.small_bags || 0}`} testId="trq-sum-bags" />
                    {price && <SumRow label={tx.qSumTotal} value={`${price}${selected.priceType ? ` · ${selected.priceType}` : ""}`} testId="trq-sum-total" />}
                    {!selected && <RoutePreview from={places.pickup} to={places.dropoff} />}
                </div>
                {selected && <p className="mt-2 text-[10px] leading-relaxed text-stone-500">{tx.qTotalNote}</p>}
            </div>

            <label className="mt-4 flex items-start gap-3 text-xs text-stone-300" data-testid="trq-privacy-wrap">
                <input data-testid="trq-privacy" type="checkbox" className={`mt-0.5 accent-amber-400 ${errors.privacy_consent ? "outline outline-1 outline-red-400" : ""}`}
                    checked={form.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} />
                {tx.qPrivacy}
            </label>

            <button type="submit" data-testid="trq-submit" disabled={sending}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-8 py-3.5 text-[11px] font-bold uppercase tracking-[0.14em] text-black transition-all hover:bg-amber-300 hover:shadow-[0_0_28px_rgba(241,211,108,0.4)] disabled:opacity-50">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sending ? tx.qSending : overCap ? tx.qSubmitAssist : tx.qSubmit}
            </button>
        </form>
    );
};
